import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { Box, Flex } from "theme-ui";
import moment from "moment-timezone";

import { getLocations } from "api/Locations";
import { getClients } from "api/Clients";
import {
  importMulchMap,
  previewImportMulchMap,
  ImportPreviewEntry,
} from "api/MulchMap";
import { pickJsonFile } from "containers/MulchCalculator/utils/importExport";
import ImportMapDedupModal, {
  EntryChoice as ImportEntryChoice,
} from "components/ImportMapDedupModal";
import ImportSitesModal from "containers/Clients/components/ImportSitesModal";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import SelectInput from "components/forms/SelectInput";
import TableData from "components/ui/TableData";
import { PlusIcon } from "components/svg";

const columnHelper = createColumnHelper();

const createColumn = (
  id: string,
  title: string,
  accessor: (row: any) => any,
  cell?: (info: any) => any,
  options = {},
) =>
  columnHelper.accessor(accessor, {
    id,
    cell: cell || ((info) => info.getValue()),
    header: () => <span>{title}</span>,
    ...options,
  });

const formatAddress = (address: any): string => {
  if (!address) return "";
  const parts = [
    address.line1,
    address.city,
    address.state,
    address.postalCode,
  ].filter(Boolean);
  return parts.join(", ");
};

/**
 * Small chip list for the Clients column. Shows up to 2 client names as
 * pills, then a "+N more" affordance for overflow. Full list on the row's
 * title attribute so hover reveals everything without needing a modal.
 */
const ClientChips: React.FC<{ clients: Array<{ id: string; name: string }> }> = ({
  clients,
}) => {
  if (!clients || clients.length === 0) {
    return (
      <span style={{ color: "#B0B4BA", fontSize: 12, fontStyle: "italic" }}>
        —
      </span>
    );
  }
  const shown = clients.slice(0, 2);
  const overflow = clients.length - shown.length;
  const allNames = clients.map((c) => c.name).join(", ");
  return (
    <Flex
      sx={{ gap: "6px", flexWrap: "wrap", alignItems: "center" }}
      title={allNames}
    >
      {shown.map((c) => (
        <span
          key={c.id}
          style={{
            display: "inline-block",
            padding: "2px 8px",
            background: "#667E4E",
            color: "#fff",
            borderRadius: 3,
            fontSize: 10,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.4,
            maxWidth: 140,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            // Session 28 hotfix: without flexShrink:0 a narrow cell was
            // squeezing the second pill down to a single letter ("K"
            // instead of "Kompan"). Now the pill keeps its full label or
            // wraps to a new line via the parent's flexWrap.
            flexShrink: 0,
          }}
        >
          {c.name}
        </span>
      ))}
      {overflow > 0 && (
        <span style={{ fontSize: 11, color: "#5D5E62" }}>+{overflow} more</span>
      )}
    </Flex>
  );
};

const Locations = () => {
  const navigate = useNavigate();
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  // Session 24: pending import waiting on the dedup-choice modal.
  const [pendingImport, setPendingImport] = useState<{
    payload: any;
    entries: ImportPreviewEntry[];
    siteLabel: string;
  } | null>(null);
  const [clientOptions, setClientOptions] = useState<
    Array<{ value: string; label: string }>
  >([{ value: "", label: "All clients" }]);
  // Session 62: reuse the plan CSV importer here with NO plan — create
  // plain Locations (staff). Bumping reloadKey remounts TableData so a
  // completed import refreshes the list.
  const [showSiteImport, setShowSiteImport] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Locations";
  }, []);

  // Populate the Client filter dropdown once on mount. Same size/rationale
  // as the editor's picker \u2014 recordsPer=500 fits ~284 active clients with
  // headroom and keeps this to a single request.
  useEffect(() => {
    let cancelled = false;
    getClients({ query: { recordsPer: 500 } }).then((res: any) => {
      if (cancelled) return;
      const items = Array.isArray(res?.items)
        ? res.items
        : Array.isArray(res)
          ? res
          : [];
      const options = items.map((c: any) => ({
        value: c.id,
        label:
          c.name ||
          c.companyName ||
          c.primaryUser?.email ||
          c.id,
      }));
      setClientOptions([{ value: "", label: "All clients" }, ...options]);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleImport = async () => {
    setImportError(null);
    const payload = await pickJsonFile();
    if (!payload) return;
    setImporting(true);
    try {
      // Session 24: preview first — same flow as Location detail.
      const preview: any = await previewImportMulchMap(payload);
      if (preview?.error) {
        setImportError(preview.error);
        return;
      }
      const hasDupes =
        Array.isArray(preview?.entries) &&
        preview.entries.some(
          (e: ImportPreviewEntry) => e.duplicateOf !== null,
        );
      if (hasDupes) {
        const siteLabel =
          payload?.location?.name ||
          payload?.location?.address?.line1 ||
          "this site";
        setPendingImport({ payload, entries: preview.entries, siteLabel });
        return;
      }
      await runImport(payload, undefined);
    } finally {
      setImporting(false);
    }
  };

  /**
   * Session 24: second half of the import flow. Called directly when no
   * duplicates were detected, or from the dedup modal's confirm callback
   * with the rep's per-entry choices.
   */
  const runImport = async (
    payload: any,
    entryChoices: Record<
      number,
      { choice: ImportEntryChoice; targetMapGroupId?: string }
    > | undefined,
  ) => {
    setImporting(true);
    setImportError(null);
    try {
      const res: any = await importMulchMap(payload, entryChoices);
      if (res?.error) {
        setImportError(res.error);
        return;
      }
      if (res?.locationId) {
        navigate(`/locations/${res.locationId}`);
      }
    } finally {
      setImporting(false);
    }
  };

  /**
   * TableData's getData spreads apiVariables first then overrides `query`
   * with its own paginated / filtered query \u2014 so setting `apiVariables`
   * doesn't help. Wrap getLocations here to force includeAssociations
   * onto every request from this page, without polluting other consumers
   * (landing recent-Locations, etc).
   */
  const getLocationsWithAssociations = useCallback(async (args: any) => {
    const q = args?.query || {};
    return getLocations({
      ...args,
      query: { ...q, includeAssociations: "true" },
    });
  }, []);

  const tableFilters = useMemo(
    () => [
      {
        // key must match props.name so TableData's filter serializer can
        // find this config when the form submits. Both are the backend's
        // pseudo-filter name (LocationService intercepts it before running
        // the standard SQLfilter path).
        key: "associatedClientId",
        component: SelectInput,
        props: {
          name: "associatedClientId",
          label: "Client",
          options: clientOptions,
          clearable: true,
          searchable: true,
          $customStyles: { minWidth: "240px" },
        },
      },
    ],
    [clientOptions],
  );

  return (
    <>
      <Header />
      <Page
        title="Locations"
        subtitle="All sites your company has mapped, quoted, or serviced."
      >
        <TableData
          key={reloadKey}
          api={getLocationsWithAssociations}
          stateKey="locations"
          defaultSortBy="updatedAt"
          defaultSortDir="desc"
          nothingFoundText="No locations found. Start by opening the Mulch Calculator."
          filters={tableFilters}
          columns={[
            createColumn(
              "name",
              "Name",
              (row: any) => row?.name,
              (info: any) => {
                const location = info.row.original;
                return (
                  <Box
                    sx={{ color: "#000", cursor: "pointer", fontWeight: 500 }}
                    onClick={() => navigate(`/locations/${location.id}`)}
                  >
                    {info.getValue() || "(unnamed)"}
                  </Box>
                );
              },
            ),
            createColumn(
              "address",
              "Address",
              (row: any) => row?.address,
              (info: any) => (
                <span style={{ color: "#555" }}>
                  {formatAddress(info.getValue())}
                </span>
              ),
            ),
            createColumn(
              "associatedClients",
              "Clients",
              (row: any) => row?.associations?.clients ?? [],
              (info: any) => <ClientChips clients={info.getValue()} />,
              { enableSorting: false },
            ),
            createColumn(
              "mapCount",
              "Maps",
              (row: any) => row?.associations?.mapCount ?? 0,
              (info: any) => {
                const v = info.getValue();
                return (
                  <span style={{ fontSize: 13, fontWeight: 500 }}>
                    {v === 0 ? (
                      <span style={{ color: "#B0B4BA" }}>0</span>
                    ) : (
                      v
                    )}
                  </span>
                );
              },
              { enableSorting: false },
            ),
            createColumn(
              "recordCount",
              "Records",
              (row: any) => row?.associations?.recordCount ?? 0,
              (info: any) => {
                const v = info.getValue();
                return (
                  <span style={{ fontSize: 13, fontWeight: 500 }}>
                    {v === 0 ? (
                      <span style={{ color: "#B0B4BA" }}>0</span>
                    ) : (
                      v
                    )}
                  </span>
                );
              },
              { enableSorting: false },
            ),
            createColumn(
              "updatedAt",
              "Last Updated",
              (row: any) => row?.updatedAt,
              (info: any) => moment(info.getValue()).format("MMM D, YYYY"),
            ),
          ]}
          actions={[
            <BasicButton
              key="duplicates"
              onClick={() => navigate("/locations/duplicates")}
              outline
              scheme="secondary"
              styles={{ marginRight: "8px" }}
              keepText
            >
              Find duplicates
            </BasicButton>,
            <BasicButton
              key="import"
              onClick={handleImport}
              outline
              scheme="secondary"
              styles={{ marginRight: "8px" }}
              $submitting={importing}
              keepText
            >
              {importing ? "Importing\u2026" : "Import map"}
            </BasicButton>,
            <BasicButton
              key="import-sites"
              onClick={() => setShowSiteImport(true)}
              outline
              scheme="secondary"
              styles={{ marginRight: "8px" }}
              keepText
            >
              Import sites (CSV)
            </BasicButton>,
            <BasicButton
              key="1"
              onClick={() => navigate("/mulch-calculator")}
              $iconHeight="10px"
            >
              <PlusIcon fill="#fff" />
              Open Mulch Calculator
            </BasicButton>,
          ]}
          title="All Locations"
        />
        {importError && (
          <Box
            sx={{
              mt: 12,
              p: "10px 14px",
              bg: "#FFF4E5",
              border: "1px solid #F5C782",
              borderRadius: "6px",
              fontSize: 13,
              color: "#8A5300",
            }}
          >
            {importError}
          </Box>
        )}
      </Page>

      {showSiteImport && (
        <ImportSitesModal
          onClose={() => setShowSiteImport(false)}
          onImported={() => setReloadKey((k) => k + 1)}
        />
      )}

      {pendingImport && (
        <ImportMapDedupModal
          siteLabel={pendingImport.siteLabel}
          entries={pendingImport.entries}
          onCancel={() => setPendingImport(null)}
          onConfirm={async (choices) => {
            const payload = pendingImport.payload;
            setPendingImport(null);
            await runImport(payload, choices);
          }}
        />
      )}
    </>
  );
};

export default Locations;
